import React, { useState } from "react";
import { Dialog, DialogTitle, DialogContent, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from '@mui/styles'
import type { Theme } from '@mui/material/styles';;

const useStyles = makeStyles((theme: Theme) => ({
	dialogTitle: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center",
		padding: theme.spacing(2),
	},
	dialogContent: {
		padding: theme.spacing(2),
		minHeight: "400px",
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
		backgroundColor: "#f5f5f5",
	},
	iframe: {
		width: "100%",
		height: "70vh",
		border: "none",
	},
	image: {
		maxWidth: "100%",
		maxHeight: "70vh",
		objectFit: "contain",
	},
	errorContainer: {
		textAlign: "center",
		padding: theme.spacing(3),
	},
	notFoundImage: {
		width: "200px",
		height: "200px",
		opacity: 0.3,
	},
}));

interface DocumentViewerModalProps {
	open: boolean;
	onClose: () => void;
	title?: string;
	documentPath?: string;
	extension?: string;
}

export const DocumentViewerModal: React.FC<DocumentViewerModalProps> = ({
	open,
	onClose,
	title = "Document Viewer",
	documentPath,
	extension = "",
}) => {
	const classes = useStyles();
	const [imageError, setImageError] = useState(false);

	// Reset error state when modal opens or document changes
	React.useEffect(() => {
		if (open) {
			setImageError(false);
		}
	}, [open, documentPath]);

	const getFullDocumentUrl = (path: string | undefined): string => {
		if (!path) return "";

		// If path already starts with http:// or https://, return as is (e.g., from URL field in upload response)
		if (path.startsWith("http://") || path.startsWith("https://")) {
			return path;
		}

		// Use relative path to uploads endpoint served by backend
		// Backend serves files at /uploads/* via static file middleware (MesfRouting.cs:57-67)
		const relativePath = path.startsWith("/") ? path.substring(1) : path;
		return `/uploads/${relativePath}`;
	};

	const renderContent = () => {
		if (!documentPath) {
			return (
				<div className={classes.errorContainer}>
					<div>No document available</div>
				</div>
			);
		}

		const fullUrl = getFullDocumentUrl(documentPath);
		const ext = extension.toUpperCase();
		const imageExtensions = ["PNG", "JPG", "JPEG", "GIF", "BMP", "SVG"];

		if (imageExtensions.includes(ext)) {
			if (imageError) {
				return (
					<div className={classes.errorContainer}>
						<svg className={classes.notFoundImage} viewBox="0 0 24 24">
							<path fill="currentColor" d="M21,19V5c0,-1.1 -0.9,-2 -2,-2H5c-1.1,0 -2,0.9 -2,2v14c0,1.1 0.9,2 2,2h14c1.1,0 2,-0.9 2,-2zM8.5,13.5l2.5,3.01L14.5,12l4.5,6H5l3.5,-4.5z"/>
						</svg>
						<p>Image not found or could not be loaded</p>
						<p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
							Path: {documentPath}
						</p>
						<a
							href={fullUrl}
							target="_blank"
							rel="noopener noreferrer"
							style={{ marginTop: "16px", display: "inline-block" }}>
							Try opening in new tab
						</a>
					</div>
				);
			}

			return (
				<img
					src={fullUrl}
					alt={title}
					className={classes.image}
					onError={() => setImageError(true)} />
			);
		}

		if (ext === "PDF") {
			return (
				<iframe
					src={fullUrl}
					title={title}
					className={classes.iframe} />
			);
		}

		return (
			<div className={classes.errorContainer}>
				<p>Document preview not available for this file type.</p>
				<a href={fullUrl} target="_blank" rel="noopener noreferrer">
					Open document in new tab
				</a>
			</div>
		);
	};

	return (
		<Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
			<DialogTitle className={classes.dialogTitle}>
				<span>{title}</span>
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<DialogContent className={classes.dialogContent}>
				{renderContent()}
			</DialogContent>
		</Dialog>
	);
};
